import type {PerkAnalyticsAccessADto} from '@cohort/admin-schemas/perkAnalytics';
import PolygonIcon from '@cohort/merchants/assets/images/polygon.png';
import PerkUsageView from '@cohort/merchants/components/perks/PerkAccessUsages';
import type {TableEmptyState} from '@cohort/merchants/components/Table';
import Table from '@cohort/merchants/components/Table';
import {usePerksUsagesListFilters} from '@cohort/merchants/hooks/filters/perksUsagesListFilters';
import type {Network} from '@cohort/shared/schema/common/blockchain';
import {blockchainExplorerUrl} from '@cohort/shared/utils/blockchain';
import {formatDate} from '@cohort/shared/utils/format';
import {getImageUrl, Sizes} from '@cohort/shared/utils/media';
import {useAutoAnimate} from '@formkit/auto-animate/react';
import {Disclosure} from '@headlessui/react';
import {Minus} from '@phosphor-icons/react';
import {CaretDown, CaretUp} from '@phosphor-icons/react';
import {Fragment, useCallback} from 'react';
import {useTranslation} from 'react-i18next';

interface Props {
  accesses: PerkAnalyticsAccessADto[];
  onSort: (key: string) => void;
  isLoading: boolean;
  emptyState: TableEmptyState;
}

const PerkAnalyticsTable = ({accesses, onSort, isLoading, emptyState}: Props): JSX.Element => {
  const {t} = useTranslation('pages', {keyPrefix: 'perks.perk.usages.perkAnalyticsTable'});
  const filters = usePerksUsagesListFilters();
  const [parent] = useAutoAnimate();

  const Headers = [
    {label: t('tableHeaderNFT'), sortingKey: 'tokenId'},
    {label: t('tableHeaderCurrentOwner'), sortingKey: 'ownerUserEmail'},
    {label: t('tableHeaderDate'), sortingKey: 'lastUsageDate'},
    {label: t('tableHeaderDetails')},
  ];

  const handleAddressClick = useCallback((network: Network, address: string) => {
    const baseUrl = blockchainExplorerUrl(network);

    window.open(`${baseUrl}/address/${address}`, '_blank');
  }, []);

  return (
    <Table
      headers={Headers}
      sortBy={filters.sort}
      onSort={onSort}
      isLoading={isLoading}
      emptyState={emptyState}
    >
      {accesses.map(access => (
        <Disclosure key={access.perkAccessId}>
          {({open}) => (
            <Fragment>
              <tr>
                <td className="flex min-w-[150px] flex-row items-center whitespace-nowrap py-2 pr-3">
                  <img
                    height={48}
                    width={48}
                    className="h-12 w-12 rounded-md"
                    src={getImageUrl(import.meta.env.COHORT_ENV, access.digitalAssetImageKey, {
                      h: Sizes.XS,
                      w: Sizes.XS,
                    })}
                  />
                  <div className="min-w-[100px] pl-3">
                    <div className="trucate text-base font-semibold leading-6">
                      #{access.tokenId}
                    </div>
                    <div className="truncate text-sm font-normal leading-5 text-slate-500">
                      {access.digitalAssetCollectionInternalName}
                    </div>
                  </div>
                </td>
                <td className="whitespace-nowrap pr-4 text-sm text-slate-500">
                  <div className="flex items-center gap-2">
                    {access.network !== null && access.ownerAddress !== null && (
                      <button
                        data-testid="order-trx-address"
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        onClick={() => handleAddressClick(access.network!, access.ownerAddress!)}
                      >
                        <img src={PolygonIcon} alt="Polygon" className="h-4 w-4" />
                      </button>
                    )}
                    {access.ownerUserEmail !== null && (
                      <div className="truncate">{access.ownerUserEmail}</div>
                    )}
                  </div>
                </td>
                <td className="whitespace-nowrap pr-4 text-left text-sm text-slate-500">
                  {access.lastUsageDate ? (
                    formatDate(access.lastUsageDate)
                  ) : (
                    <Minus className="h-4 w-4" />
                  )}
                </td>
                <td className="w-64 items-center whitespace-nowrap text-left text-sm leading-5 text-primary">
                  {access.usages.length > 0 && (
                    <Disclosure.Button>
                      {open ? (
                        <span>
                          {t('buttonHideDetails')}
                          <CaretUp className="ml-2 inline h-4 w-4" />
                        </span>
                      ) : (
                        <span>
                          {t('buttonShowDetails')}
                          <CaretDown className="mb-1 inline h-4 w-4" />
                        </span>
                      )}
                    </Disclosure.Button>
                  )}
                </td>
              </tr>
              {
                <tr ref={parent}>
                  <Disclosure.Panel as="td" colSpan={5}>
                    {access.usages.map(usage => (
                      <PerkUsageView usage={usage} key={usage.perkUsageId} />
                    ))}
                  </Disclosure.Panel>
                </tr>
              }
            </Fragment>
          )}
        </Disclosure>
      ))}
    </Table>
  );
};

export default PerkAnalyticsTable;
